<script lang="ts">
import {computed, defineComponent, onMounted, Ref, ref} from '@vue/composition-api'

import coreApi from '@/api/core.api'
import CardWrapper from '@/components/general/CardWrapper.vue'
import RegistrationCard from '@/components/general/RegistrationCard.vue'
import {RegistrationPlatform, RegistrationVehicleStatus, ResultStatus} from '@/types'
import {AttachmentReason} from '@/types/dynamodb-types'
import {RegistrationTypes, VinRegistrationStates} from '@/utils/constants'
import {mapGetters} from '@/utils/map-store'

import DialogMapitRegistration from '../dialog/DialogMapitRegistration.vue'
import DialogResult from '../dialog/DialogResult.vue'
import DialogVinRegistration from '../dialog/DialogVinRegistration.vue'

interface RegistrationCardData {
  name: string
  image: string
  imageHover: string
  title: string
  subtitle: string
  description: string
  actionText: string
  status: RegistrationVehicleStatus
  platform: RegistrationPlatform
}

export default defineComponent({
  name: 'RegistrationOnlyMapit',
  components: {
    CardWrapper,
    RegistrationCard,
    DialogVinRegistration,
    DialogMapitRegistration,
    DialogResult,
  },
  setup(props, {root}) {
    const {getCapabilities: capabilities} = mapGetters(root.$store.getters)

    const registrationCards = ref<Array<RegistrationCardData>>([])

    const isTransferredAvailable = computed<boolean>(() => {
      return capabilities.value?.dealerDeviceTransfer?.mode === 'FULL'
    })

    const cancelDialog = ref(false)
    const dialogVinRegistration = ref(false)
    const dialogMapitRegistration = ref(false)
    const vinData = ref(null) as Ref<any>
    const dialogResult = ref(false)
    const dialogResultStatus = ref(null) as Ref<ResultStatus | null>
    const dialogVinLoading = ref(false)
    const statusSuccessStrings = ref({
      title: root.$t('honda.registration.mapit-wizard.used.confirm_popup.title'),
      body: root.$t('honda.registration.mapit-wizard.used.confirm_popup.body'),
    })
    const statusErrorStrings = ref({
      title: root.$t('honda.registration.mapit-wizard.used.error_popup.title'),
      body: root.$t('honda.registration.mapit-wizard.used.error_popup.body'),
    })
    const resultLoading = ref(false)
    const queryVin = ref('')

    const getResultStrings = computed(() => {
      return dialogResultStatus.value === ResultStatus.SUCCESS ? statusSuccessStrings.value : statusErrorStrings.value
    })

    const buttonStyles = computed(() => {
      return `
        text-transform: uppercase !important;
        padding: 8px 24px !important;
        font-family: 'ProximaNovaExCnSbold', sans-serif !important;
        letter-spacing: 0.01em !important;
     `
    })

    onMounted(async () => {
      await root.$store.dispatch('resetRegistration')
      const availableCards = [
        {
          key: 'new',
          status: 'NEW',
          platform: 'HONDA_MAPIT',
        },
        {
          key: 'used-demo-bike',
          status: 'USED',
          platform: 'HONDA_MAPIT',
        },
        isTransferredAvailable.value
          ? {
              key: 'transferred',
              status: 'TRANSFERRED',
              platform: 'HONDA_MAPIT',
            }
          : undefined,
        {
          key: 'other',
          status: 'NEW',
          platform: 'MAPIT_CONNECT',
        },
      ].filter(card => card !== undefined) as Array<{
        key: string
        status: RegistrationVehicleStatus
        platform: RegistrationPlatform
      }>

      registrationCards.value = availableCards.map(({key, status, platform}) => ({
        name: key,
        image: `/images/registrations/${key}.svg`,
        imageHover: `/images/registrations/${key}-light.svg`,
        title: `honda.registration.mapit-wizard.${key}.title`,
        subtitle: `honda.registration.mapit-wizard.${key}.subtitle`,
        description: `honda.registration.mapit-wizard.${key}.description`,
        actionText: `honda.registration.mapit-wizard.${key}.action`,
        status,
        platform,
      }))

      if (root.$route.query.vin) {
        queryVin.value = root.$route.query.vin as string
        dialogVinRegistration.value = true
      }
    })

    function handleActionClick(card: RegistrationCardData) {
      if (card.status === 'TRANSFERRED') root.$router.push({name: 'TransferredVehicles'})
      else if (card.status === 'USED') dialogVinRegistration.value = true
      else if (card.platform === 'MAPIT_CONNECT') root.$router.push({name: 'RegistrationMapitConnect'})
      else root.$router.push({name: 'HondaRegistration', query: {forcedType: card.platform}})
    }

    function closeDialogVinRegistration() {
      dialogVinRegistration.value = false
      vinData.value = null
    }

    async function continueDialogVinRegistration(vData: any) {
      if (vData.status === VinRegistrationStates.NOT_FOUND) {
        vinData.value = null
        dialogVinLoading.value = true
        await root.$router.push({
          name: 'HondaRegistration',
          query: {forcedType: RegistrationTypes.HONDA_MAPIT, vin: vData.vin},
        })
        dialogVinRegistration.value = false
      } else {
        dialogVinRegistration.value = false
        dialogMapitRegistration.value = true
        vinData.value = vData
      }
    }

    function closeDialogMapitRegistration() {
      dialogMapitRegistration.value = false
      vinData.value = null
    }

    function continueDialogMapitRegistration(mapit: any) {
      if (!resultLoading.value) {
        resultLoading.value = true
        const body = {
          vehicle: {
            id: vinData.value?.vehicleId,
          },
          reason: AttachmentReason.AfterRegistrationAuthorised,
        }
        coreApi
          .addDeviceToVehicle(mapit.id, body)
          .then(res => {
            dialogResultStatus.value = ResultStatus.SUCCESS
          })
          .catch(err => {
            dialogResultStatus.value = ResultStatus.ERROR
          })
          .finally(() => {
            closeDialogMapitRegistration()
            resultLoading.value = false
            dialogResult.value = true
          })
      }
    }

    function closeDialogResult() {
      dialogResult.value = false
    }

    function initRegistration() {
      if (root.$route.query.vin) {
        queryVin.value = root.$route.query.vin as string
        dialogVinRegistration.value = true
      }
    }

    return {
      registrationCards,
      isTransferredAvailable,
      cancelDialog,
      dialogVinRegistration,
      dialogMapitRegistration,
      vinData,
      dialogResult,
      dialogResultStatus,
      dialogVinLoading,
      statusSuccessStrings,
      statusErrorStrings,
      resultLoading,
      queryVin,
      getResultStrings,
      buttonStyles,
      handleActionClick,
      closeDialogVinRegistration,
      continueDialogVinRegistration,
      closeDialogMapitRegistration,
      continueDialogMapitRegistration,
      closeDialogResult,
      initRegistration,
    }
  },
})
</script>

<template>
  <div>
    <card-wrapper
      id="registration-only-mapit"
      :title="$t('honda.registration.mapit-wizard.title')"
      :show-divider="true"
      :hide-close-btn="true">
      <h2 class="only-mapit-registration-subtitle">{{ $t('honda.registration.mapit-wizard.subtitle') }}</h2>
      <transition-group class="card-grid" name="slide-in" tag="div" :style="{'--total': registrationCards.length}">
        <registration-card v-for="(card, i) in registrationCards" :key="card.name" :style="{'--i': i}">
          <template #icon="{isHovering}">
            <transition name="fade">
              <img v-if="!isHovering" class="img-card" :src="card.image" :alt="card.name" :key="`img-card-${i}`" />
              <img v-else class="img-card" :src="`${card.imageHover}`" :alt="card.name" :key="`img-card-${i}-light`" />
            </transition>
          </template>
          <template #subtitle>
            {{ $t(card.subtitle) }}
          </template>
          <template #title>
            {{ $t(card.title) }}
          </template>
          <template #description>
            {{ $t(card.description) }}
          </template>
          <template #actions>
            <m-btn
              :id="`action-button-${card.name}`"
              color="primary"
              class="button-padding action-button"
              @click="handleActionClick(card)"
              :style="buttonStyles">
              {{ $t(card.actionText) }}
            </m-btn>
          </template>
        </registration-card>
      </transition-group>
      <dialog-vin-registration
        :dialog="dialogVinRegistration"
        @continue="continueDialogVinRegistration"
        @close="closeDialogVinRegistration"
        :queryVin="queryVin"
        :loading="dialogVinLoading"
        :skipEligibilityCheck="true" />
      <dialog-mapit-registration
        :dialog="dialogMapitRegistration"
        :loading="resultLoading"
        :account="vinData && vinData.owner"
        @continue="continueDialogMapitRegistration"
        @close="closeDialogMapitRegistration" />
      <dialog-result
        :dialog="dialogResult"
        :status="dialogResultStatus"
        :textTitle="getResultStrings.title"
        :textBody="getResultStrings.body"
        @nextHandler="closeDialogResult"
        @closeHandler="closeDialogResult" />
    </card-wrapper>
  </div>
</template>

<style lang="scss" scoped>
.only-mapit-registration-subtitle {
  font-size: 16px;
  font-family: 'ProximaNovaRegular', sans-serif;
  font-weight: 400;
  line-height: 50px;
  text-align: center;
  color: hsla(0, 0%, 13%, 1);
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, 224px);
  grid-template-rows: 362px;
  place-content: center;
  grid-gap: 20px;
  justify-items: center;
  align-items: center;
  width: 100%;
  padding: 20px 0;
  margin-bottom: 48px;
}

.action-button {
  transition: all 0.2s ease-in-out;
  font-size: 20px !important;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0px 8px 8px 0px hsla(0, 0%, 0%, 0.25) !important;
  }
}

.img-card {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, 0);
}

.fade {
  &-enter-active,
  &-leave-active {
    transition: opacity 0.5s;
  }
  &-enter,
  &-leave-to {
    opacity: 0;
  }
}

.slide-in {
  &-move {
    transition:
      opacity 0.5s linear,
      transform 1s ease-in-out;
  }

  &-leave-active {
    transition:
      opacity 0.4s linear,
      transform 0.4s cubic-bezier(0.5, 0, 0.7, 0.4);
    transition-delay: calc(0.15s * (var(--total) - var(--i)));
  }

  &-enter-active {
    transition:
      opacity 0.5s linear,
      transform 1s cubic-bezier(0.2, 0.5, 0.1, 1);
    transition-delay: calc(0.15s * var(--i));
  }

  &-enter,
  &-leave-to {
    opacity: 0;
  }

  &-enter {
    transform: translateY(1em);
  }

  &-leave-to {
    transform: translateY(-1em);
  }
}
</style>
